import { useCallback, useState } from 'react';
import { useStore } from 'react-redux';
import { BlobReader, BlobWriter, ZipReader, type FileEntry } from '@zip.js/zip.js';
import pLimit from 'p-limit';
import { datadogRum } from '@datadog/browser-rum';
import userServiceApi from 'shared/api/user.service.api';
import { EVENTS } from 'shared/constants';
import analyticsService from 'src/helpers/analytics.service';
import { MAX_AVATAR_BYTES } from 'src/components/avatar/avatar.constants';
import {
  BULK_AVATAR_STATUS,
  type BulkAvatarStatus,
  type UploadFailure,
  type UserWithEmails,
  buildEmailToUserIdMap,
  classifyEntry,
  isCandidateEntry,
  mapUploadFailure,
  mimeTypeForFileName,
} from './bulk-avatar-upload.utils';

export const BULK_AVATAR_STAGES = {
  UPLOAD: 'UPLOAD',
  PROCESSING: 'PROCESSING',
  RESULTS: 'RESULTS',
} as const;

export type BulkAvatarStage = (typeof BULK_AVATAR_STAGES)[keyof typeof BULK_AVATAR_STAGES];

export const BULK_AVATAR_PROCESS_ERRORS = {
  EMPTY_ZIP: 'EMPTY_ZIP',
  PARSE_FAILED: 'PARSE_FAILED',
} as const;

export interface BulkAvatarResult {
  id: number;
  fileName: string;
  email: string;
  status: BulkAvatarStatus;
  message?: string;
}

export interface BulkAvatarProcessError {
  type: (typeof BULK_AVATAR_PROCESS_ERRORS)[keyof typeof BULK_AVATAR_PROCESS_ERRORS];
  detail?: string;
}

interface BulkAvatarItem {
  id: number;
  entry: FileEntry;
  fileName: string;
  email: string;
  userId?: string;
  status: BulkAvatarStatus | null;
  message?: string;
}

const UPLOAD_CONCURRENCY = 4;
// A black-holed request becomes a failed row instead of freezing the
// non-closable progress modal.
const UPLOAD_TIMEOUT_MS = 60000;
const TELEMETRY_CONTEXT = { feature: 'bulk-avatar-upload' };

async function uploadEntry({ entry, fileName, userId }: BulkAvatarItem): Promise<UploadFailure> {
  if (entry.uncompressedSize > MAX_AVATAR_BYTES) {
    return { status: BULK_AVATAR_STATUS.TOO_LARGE };
  }
  const blob = await entry.getData(new BlobWriter());
  const file = new File([blob], fileName, { type: mimeTypeForFileName(fileName) });
  const { data, error } = await userServiceApi.uploadAvatar({
    userId,
    file,
    timeout: UPLOAD_TIMEOUT_MS,
  });
  if (error) {
    if (!error.response && !error.request) {
      datadogRum.addError(error, TELEMETRY_CONTEXT);
    }
    return mapUploadFailure({ data, error });
  }
  if (data?.user?.avatarPublicId) {
    return { status: BULK_AVATAR_STATUS.UPLOADED };
  }
  datadogRum.addError(
    new Error('Bulk avatar upload: success response missing user.avatarPublicId'),
    TELEMETRY_CONTEXT,
  );
  return { status: BULK_AVATAR_STATUS.UPLOAD_FAILED };
}

function trackCompletion(results: BulkAvatarResult[]) {
  const countsByStatus = results.reduce<Record<string, number>>(
    (counts, { status }) => ({ ...counts, [status]: (counts[status] ?? 0) + 1 }),
    {},
  );
  analyticsService.track(EVENTS.USER_PROFILE.BULK_UPLOAD_PHOTOS, {
    total: results.length,
    ...countsByStatus,
  });
}

export function useBulkAvatarUpload() {
  const store = useStore();
  const [stage, setStage] = useState<BulkAvatarStage>(BULK_AVATAR_STAGES.UPLOAD);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<BulkAvatarResult[]>([]);
  const [processError, setProcessError] = useState<BulkAvatarProcessError | null>(null);

  const reset = useCallback(() => {
    setStage(BULK_AVATAR_STAGES.UPLOAD);
    setProgress({ done: 0, total: 0 });
    setResults([]);
    setProcessError(null);
  }, []);

  const processZip = useCallback(
    async (zipFile: File) => {
      setStage(BULK_AVATAR_STAGES.PROCESSING);
      setProcessError(null);

      let finished: BulkAvatarResult[];
      try {
        const zipReader = new ZipReader(new BlobReader(zipFile));
        const entries = await zipReader.getEntries();
        // Read at call time so the map reflects the freshest fetched list.
        const state = store.getState() as { usersManagement?: { list?: UserWithEmails[] | null } };
        const emailToUserId = buildEmailToUserIdMap(state.usersManagement?.list);
        const seenEmails = new Set<string>();
        const items: BulkAvatarItem[] = entries
          .filter(
            (entry): entry is FileEntry => !entry.directory && isCandidateEntry(entry.filename),
          )
          .map((entry, id) => {
            const fileName = entry.filename.split('/').pop() ?? entry.filename;
            const { email, userId, status } = classifyEntry({
              fileName,
              seenEmails,
              emailToUserId,
            });
            return { id, entry, fileName, email, userId, status };
          });

        if (!items.length) {
          setProcessError({ type: BULK_AVATAR_PROCESS_ERRORS.EMPTY_ZIP });
          setStage(BULK_AVATAR_STAGES.UPLOAD);
          return false;
        }

        const pending = items.filter(item => !item.status);
        setProgress({ done: 0, total: pending.length });

        const limit = pLimit(UPLOAD_CONCURRENCY);
        await Promise.all(
          pending.map(item =>
            limit(async () => {
              const failure: UploadFailure = await uploadEntry(item).catch((error: Error) => {
                datadogRum.addError(error, TELEMETRY_CONTEXT);
                return { status: BULK_AVATAR_STATUS.UPLOAD_FAILED, message: error?.message };
              });
              item.status = failure.status;
              item.message = failure.message;
              setProgress(prev => ({ ...prev, done: prev.done + 1 }));
            }),
          ),
        );
        await zipReader.close();

        finished = items.map(({ id, fileName, email, status, message }) => ({
          id,
          fileName,
          email,
          status: status ?? BULK_AVATAR_STATUS.UPLOAD_FAILED,
          message,
        }));
      } catch (error) {
        datadogRum.addError(error, TELEMETRY_CONTEXT);
        setProcessError({
          type: BULK_AVATAR_PROCESS_ERRORS.PARSE_FAILED,
          detail: error instanceof Error ? error.message : String(error),
        });
        setStage(BULK_AVATAR_STAGES.UPLOAD);
        return false;
      }

      setResults(finished);
      setStage(BULK_AVATAR_STAGES.RESULTS);
      try {
        trackCompletion(finished);
      } catch (error) {
        datadogRum.addError(error, TELEMETRY_CONTEXT);
      }
      return true;
    },
    [store],
  );

  return { stage, progress, results, processError, processZip, reset };
}
